import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import UndoIcon from "@mui/icons-material/Undo";

import axiosInstance from "../../axios/axiosInstance";

function TimeSlotSelectionDialog({ open, onClose, interview, onSuccess }) {
  const [selectedSlots, setSelectedSlots] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && interview) {
      // Initialize selected slots state
      const initialSlots = {};
      interview.stages?.forEach((stage) => {
        if (
          stage.candidateAction === "selected" ||
          (stage.date && stage.time && stage.candidateAction !== "rejected")
        ) {
          initialSlots[stage.stageName] = {
            action: "selected",
            selectedSlot: stage.candidateSelectedSlot || {
              date: stage.date,
              time: stage.time,
              slotId: "existing",
            },
            rejectionReason: "",
          };
        } else if (stage.candidateAction === "rejected") {
          initialSlots[stage.stageName] = {
            action: "rejected",
            selectedSlot: null,
            rejectionReason: stage.rejectionReason || "",
          };
        } else {
          initialSlots[stage.stageName] = {
            action: "none",
            selectedSlot: null,
            rejectionReason: "",
          };
        }
      });
      setSelectedSlots(initialSlots);
      setError("");
    }
  }, [open, interview]);

  const handleSlotSelection = (stageName, day, slot) => {
    setSelectedSlots((prev) => ({
      ...prev,
      [stageName]: {
        action: "selected",
        selectedSlot: { date: day, ...slot },
        rejectionReason: "",
      },
    }));
  };

  const handleRejectSlots = (stageName) => {
    setSelectedSlots((prev) => ({
      ...prev,
      [stageName]: {
        action: "rejected",
        selectedSlot: null,
        rejectionReason: "",
      },
    }));
  };

  const handleRejectionReasonChange = (stageName, reason) => {
    setSelectedSlots((prev) => ({
      ...prev,
      [stageName]: {
        ...prev[stageName],
        rejectionReason: reason,
      },
    }));
  };

  const handleUndoRejection = (stageName) => {
    setSelectedSlots((prev) => ({
      ...prev,
      [stageName]: {
        action: "none",
        selectedSlot: null,
        rejectionReason: "",
      },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      // Filter for active stages that are in the proposal phase
      const activeStages = interview.stages.filter(
        (stage) => stage.status === "slots_proposed",
      );

      if (activeStages.length === 0) {
        setError("No active stages found for slot selection.");
        setLoading(false);
        return;
      }

      // Validate rejection reasons for active stages
      for (const stage of activeStages) {
        const selection = selectedSlots[stage.stageName];
        if (
          selection?.action === "rejected" &&
          !selection.rejectionReason?.trim()
        ) {
          setError(
            `Please provide a reason for rejecting slots for ${stage.stageName}`,
          );
          setLoading(false);
          return;
        }
      }

      // Identify changed stages (stages where an action has been taken)
      const changedStages = activeStages.filter((stage) => {
        const selection = selectedSlots[stage.stageName];
        return (
          selection &&
          (selection.action === "selected" || selection.action === "rejected")
        );
      });

      if (changedStages.length === 0) {
        setError(
          "Please select a slot or reject slots for at least one stage.",
        );
        setLoading(false);
        return;
      }

      // Create payloads for changed stages
      const promises = changedStages.map((stage) => {
        const selection = selectedSlots[stage.stageName];
        let payload = {};

        if (selection.action === "selected" && selection.selectedSlot) {
          payload = {
            stageName: stage.stageName,
            date: selection.selectedSlot.date,
            time: selection.selectedSlot.time,
            proposedSlots: [],
            candidateAction: "selected",
          };
        } else if (selection.action === "rejected") {
          payload = {
            stageName: stage.stageName,
            date: null,
            time: null,
            proposedSlots: [],
            candidateSelectedSlot: null,
            candidateAction: "rejected",
            rejectionReason: selection.rejectionReason,
          };
        }

        return axiosInstance.post(
          `/api/profile/interviews/${interview.interviewId}/stages`,
          payload,
        );
      });

      await Promise.all(promises);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Error submitting time slot selection:", err);
      setError(
        err.response?.data?.message ||
        "Failed to submit your selection. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Check if ALL stages have an action (selected or rejected)
  const hasAllActionsCompleted = () => {
    if (!interview?.stages || interview.stages.length === 0) return false;

    return interview.stages.every((stage) => {
      const selection = selectedSlots[stage.stageName];
      if (!selection) return false;

      if (selection.action === "selected") return true;
      if (selection.action === "rejected") {
        return (
          selection.rejectionReason &&
          selection.rejectionReason.trim().length > 0
        );
      }
      return false;
    });
  };

  // Check if all stages are rejected
  const areAllStagesRejected = () => {
    if (!interview?.stages || interview.stages.length === 0) return false;

    return interview.stages.every((stage) => {
      const selection = selectedSlots[stage.stageName];
      return selection && selection.action === "rejected";
    });
  };

  if (!interview) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          borderBottom: "2px solid",
          borderColor: "divider",
          backgroundColor: "#6c5ce7",
          color: "white",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Select Interview Time Slots
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
      >
        <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            {interview.stages
              ?.filter((stage) => stage.status === "slots_proposed")
              .map((stage, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "primary.main", mb: 0.5 }}
                      >
                        {stage.stageName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Interviewer: {Array.isArray(stage.interviewers) ? stage.interviewers.join(", ") : stage.interviewer}
                      </Typography>
                    </Box>
                    {selectedSlots[stage.stageName]?.action === "selected" && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Slot Selected"
                        size="small"
                        sx={{
                          backgroundColor: "#8c5eb4ff",
                          color: "#FFFFFF",
                          "& .MuiChip-icon": {
                            color: "#FFFFFF",
                          },
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {selectedSlots[stage.stageName]?.action === "rejected" && (
                      <Chip
                        icon={<CancelIcon />}
                        label="Rejected"
                        size="small"
                        sx={{
                          backgroundColor: "#ba68c8",
                          color: "#FFFFFF",
                          "& .MuiChip-icon": {
                            color: "#FFFFFF",
                          },
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {stage.candidateAction === "selected" ||
                    (stage.date &&
                      stage.time &&
                      stage.candidateAction !== "rejected") ? (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#e8f5e9",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "#c8e6c9",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                        Time slot scheduled for this stage
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Date:
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {new Date(
                              stage.candidateSelectedSlot?.date || stage.date,
                            ).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Time:
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {stage.candidateSelectedSlot?.time || stage.time}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : stage.proposedSlots && stage.proposedSlots.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        Available Time Slots:
                      </Typography>
                      <RadioGroup
                        value={
                          selectedSlots[stage.stageName]?.selectedSlot
                            ? `${selectedSlots[stage.stageName].selectedSlot.date
                            }-${selectedSlots[stage.stageName].selectedSlot
                              .slotId
                            }`
                            : ""
                        }
                      >
                        <Stack spacing={2}>
                          {stage.proposedSlots.map((daySlot, dayIndex) => (
                            <Box key={dayIndex}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "primary.main",
                                  mb: 1,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <EventNoteIcon sx={{ mr: 1, fontSize: 18 }} />
                                {new Date(daySlot.date).toLocaleDateString(
                                  "en-GB",
                                  {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )}
                              </Typography>
                              <Stack spacing={1} sx={{ pl: 3 }}>
                                {daySlot.slots?.map((slot, slotIndex) => (
                                  <FormControlLabel
                                    key={slotIndex}
                                    value={`${daySlot.date}-${slot.slotId}`}
                                    control={<Radio />}
                                    label={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <AccessTimeIcon
                                          sx={{
                                            mr: 1,
                                            fontSize: 16,
                                            color: "text.secondary",
                                          }}
                                        />
                                        {slot.time}
                                      </Box>
                                    }
                                    onChange={() =>
                                      handleSlotSelection(
                                        stage.stageName,
                                        daySlot.date,
                                        slot,
                                      )
                                    }
                                    disabled={false}
                                    sx={{
                                      backgroundColor: "background.paper",
                                      borderRadius: 1,
                                      px: 2,
                                      py: 1,
                                      m: 0,
                                      border: "1px solid",
                                      borderColor:
                                        selectedSlots[stage.stageName]
                                          ?.selectedSlot?.slotId === slot.slotId
                                          ? "primary.main"
                                          : "divider",
                                      "&:hover": {
                                        backgroundColor: "action.hover",
                                      },
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </RadioGroup>

                      {selectedSlots[stage.stageName]?.action ===
                        "rejected" && (
                          <Box sx={{ mt: 2 }}>
                            <TextField
                              fullWidth
                              label="Reason for Rejection"
                              placeholder="Please explain why none of the proposed slots work for you..."
                              multiline
                              rows={2}
                              value={
                                selectedSlots[stage.stageName]?.rejectionReason ||
                                ""
                              }
                              onChange={(e) =>
                                handleRejectionReasonChange(
                                  stage.stageName,
                                  e.target.value,
                                )
                              }
                              required
                              error={
                                !selectedSlots[
                                  stage.stageName
                                ]?.rejectionReason?.trim()
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#ba68c8",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#8c5eb4ff",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#ba68c8",
                                  },
                                  "&.Mui-error fieldset": {
                                    borderColor: "#ba68c8",
                                  },
                                },
                                "& .MuiInputLabel-root": {
                                  color: "#ba68c8",
                                  "&.Mui-error": {
                                    color: "#ba68c8",
                                  },
                                },
                              }}
                            />
                          </Box>
                        )}

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        {selectedSlots[stage.stageName]?.action ===
                          "rejected" ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={() => handleUndoRejection(stage.stageName)}
                            sx={{
                              textTransform: "none",
                              color: "#ba68c8",
                              borderColor: "#ba68c8",
                              "&:hover": {
                                borderColor: "#8c5eb4ff",
                                backgroundColor: "rgba(186, 104, 200, 0.04)",
                              },
                            }}
                          >
                            Undo Rejection
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => handleRejectSlots(stage.stageName)}
                            sx={{
                              textTransform: "none",
                              color: "#ba68c8",
                              borderColor: "#ba68c8",
                              "&:hover": {
                                borderColor: "#8c5eb4ff",
                                backgroundColor: "rgba(186, 104, 200, 0.04)",
                              },
                            }}
                          >
                            Reject All Slots
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Alert severity="warning">
                      No time slots have been proposed for this stage yet.
                    </Alert>
                  )}
                </Paper>
              ))}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 120, fontWeight: 600, textTransform: "none" }}
        >
          {loading ? "Submitting..." : "Submit Selection"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TimeSlotSelectionDialog;
